/**
 * <tryout-bundle> — picker for the SybaSoil Try-Out Bundle PDP.
 *
 * State machine:
 *   - Tracks per-card quantity in component state (Map). Out-of-stock
 *     variants (cards with data-out-of-stock="true") are excluded from
 *     state and from the +/- bindings.
 *   - Recomputes totals + progress + tier on every click.
 *   - Multi-tier model: tiers[] is read from data-tiers (JSON) on the
 *     host element — each entry is { qty, percent }. The CTA enables
 *     once totalQty >= the smallest tier qty (tierMin).
 *   - Atomically posts ALL non-zero quantities to /cart/add.js when the
 *     customer clicks Add. Single network round-trip — the request includes
 *     `sections` so the response carries the rendered cart-drawer markup,
 *     which we hand straight to the theme's <cart-drawer> custom element.
 *     No /cart navigation, no cart re-fetch, no cart re-render flicker.
 */
class TryoutBundle extends HTMLElement {
  connectedCallback() {
    // Mark body so CSS can suppress competing widgets (e.g. KaChing) on PDPs
    // that host this picker. CSS uses both this class and `:has()` for compat.
    document.body.classList.add('has-tryout-bundle');

    // Tier table: parsed from data-tiers, e.g. [{"qty":3,"percent":15},…].
    // Falls back to a single 3+/20% tier if the attribute is missing or malformed.
    let tiers = [{ qty: 3, percent: 20 }];
    try {
      const raw = this.dataset.tiers;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          tiers = parsed
            .map((t) => ({ qty: parseInt(t.qty, 10), percent: parseInt(t.percent, 10) }))
            .filter((t) => Number.isFinite(t.qty) && Number.isFinite(t.percent))
            .sort((a, b) => a.qty - b.qty);
        }
      }
    } catch (e) {
      console.warn('[tryout-bundle] could not parse data-tiers, using default', e);
    }
    this.tiers = tiers;
    this.tierMin = this.tiers[0].qty;
    this.tierMax = this.tiers[this.tiers.length - 1].qty;

    // OOS-aware card binding: the snippet marks unavailable variants with
    // data-out-of-stock="true" and renders a sold-out badge instead of the
    // qty stepper, so they have no [data-action] children to bind anyway.
    // We still skip them explicitly here so they don't pollute state.
    const allCards = Array.from(this.querySelectorAll('.tryout-bundle__card[data-variant-id]'));
    this.cards = allCards.filter((c) => c.dataset.outOfStock !== 'true');
    this.state = new Map(); // variantId (string) -> qty (number)

    this.totalQtyEl = this.querySelector('[data-total-qty]');
    this.totalListEl = this.querySelector('[data-total-list]');
    this.savingsWrap = this.querySelector('[data-savings]');
    this.savingsAmountEl = this.querySelector('[data-savings-amount]');
    this.ctaBtn = this.querySelector('[data-add-to-cart]');
    this.ctaLabelEl = this.querySelector('[data-cta-label]');
    this.progressFillEl = this.querySelector('[data-progress-fill]');
    this.progressMessageEl = this.querySelector('[data-progress-message]');
    this.progressSteps = Array.from(this.querySelectorAll('.tryout-bundle__progress-step'));

    // All translatable strings come from data-i18n-* attributes on the host
    // element. Liquid renders them server-side from locales/<lang>.json, so
    // they're already in the customer's language by the time we read them.
    // Templates contain {count} and {percent} placeholders — JS substitutes
    // at runtime.
    this.i18n = {
      pickMoreOne: this.dataset.i18nPickMoreOne || 'Pick 1 more bag to unlock {percent}% off',
      pickMoreMany: this.dataset.i18nPickMoreMany || 'Pick {count} more bags to unlock {percent}% off',
      addOne: this.dataset.i18nAddOne || 'Add 1 bag to cart',
      addMany: this.dataset.i18nAddMany || 'Add {count} bags to cart',
      progressOne: this.dataset.i18nProgressOne || '1 more bag to unlock {percent}% off',
      progressMany: this.dataset.i18nProgressMany || '{count} more bags to unlock {percent}% off',
      unlocked: this.dataset.i18nUnlocked || '{percent}% off unlocked!',
      // Multi-tier nudge: "X% off unlocked! Add N more for Y%" (when there's a higher tier left)
      unlockedNext: this.dataset.i18nUnlockedNext
        || '{percent}% off unlocked! Add {count} more for {nextPercent}%',
      adding: this.dataset.i18nAdding || 'Adding…',
      error: this.dataset.i18nError || 'Could not add — try again',
    };

    // Capture the merchant-configured idle-state label (e.g. "Select at
    // least 3 bags") from the initial server-rendered HTML.
    this.idleCtaLabel = (this.ctaLabelEl?.textContent || '').trim();
    this.idleProgressMessage = (this.progressMessageEl?.textContent || this.idleCtaLabel).trim();

    this.bindCards();
    if (this.ctaBtn) {
      this.ctaBtn.addEventListener('click', () => this.handleAddToCart());
    }

    this.recompute();
  }

  /** Substitute {count}, {percent}, {nextPercent} placeholders in a localized template. */
  fmt(tpl, vars) {
    let out = tpl;
    if (vars) {
      if (typeof vars.count === 'number') out = out.replace(/\{count\}/g, vars.count);
      if (typeof vars.percent === 'number') out = out.replace(/\{percent\}/g, vars.percent);
      if (typeof vars.nextPercent === 'number') out = out.replace(/\{nextPercent\}/g, vars.nextPercent);
    }
    return out;
  }

  bindCards() {
    this.cards.forEach((card) => {
      const variantId = card.dataset.variantId;
      this.state.set(variantId, 0);

      card.querySelectorAll('[data-action]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const action = e.currentTarget.dataset.action;
          const current = this.state.get(variantId) || 0;
          const next = action === 'increment' ? current + 1 : Math.max(0, current - 1);
          this.state.set(variantId, next);
          this.recompute();
        });
      });
    });
  }

  /** Returns the highest-percent tier whose qty threshold is met, or null. */
  currentTier(totalQty) {
    let active = null;
    for (const t of this.tiers) {
      if (totalQty >= t.qty) active = t;
    }
    return active;
  }

  /** Returns the next tier above the active one, or null if at peak. */
  nextTier(totalQty) {
    for (const t of this.tiers) {
      if (totalQty < t.qty) return t;
    }
    return null;
  }

  /**
   * Maps current totalQty to a 0..1 fill scale on the progress track using
   * piecewise linear interpolation between dot positions.
   *
   * Track geometry: with N evenly-distributed columns and the track running
   * from the LEFT edge of the steps container to the LAST dot's center,
   *   dot-i's position on the track  =  (2i + 1) / (2N - 1)
   * For N=3 → [0.20, 0.60, 1.00].
   *
   * Checkpoints: { qty: 0, pos: 0 } → { qty: tier1.qty, pos: dot1Pos }
   *           → { qty: tier2.qty, pos: dot2Pos } → … → { tierN.qty, pos: 1 }
   * Between two checkpoints we interpolate linearly so the fill rate is
   * predictable and the dot is reached precisely at its qty threshold.
   */
  computeFillScale(totalQty) {
    if (totalQty <= 0) return 0;
    const n = this.tiers.length;
    if (n === 0) return 0;
    const dotPos = (i) => (2 * i + 1) / (2 * n - 1);
    const checkpoints = [{ qty: 0, pos: 0 }];
    this.tiers.forEach((t, i) => checkpoints.push({ qty: t.qty, pos: dotPos(i) }));
    for (let i = 1; i < checkpoints.length; i++) {
      const prev = checkpoints[i - 1];
      const curr = checkpoints[i];
      if (totalQty <= curr.qty) {
        const segLen = curr.qty - prev.qty;
        const frac = segLen > 0 ? (totalQty - prev.qty) / segLen : 1;
        return prev.pos + frac * (curr.pos - prev.pos);
      }
    }
    return 1;
  }

  recompute() {
    let totalQty = 0;
    let totalListCents = 0;

    this.cards.forEach((card) => {
      const variantId = card.dataset.variantId;
      const unitCents = parseInt(card.dataset.unitPriceCents, 10) || 0;
      const qty = this.state.get(variantId) || 0;

      const qtyEl = card.querySelector('[data-qty-display]');
      const minusBtn = card.querySelector('[data-action="decrement"]');
      if (qtyEl) qtyEl.textContent = String(qty);
      if (minusBtn) minusBtn.disabled = qty === 0;
      card.classList.toggle('tryout-bundle__card--active', qty > 0);

      totalQty += qty;
      totalListCents += unitCents * qty;
    });

    // Progress bar fill — piecewise linear interpolation between checkpoints
    // so each dot is reached EXACTLY when its qty threshold is met. The CSS
    // track extends from the LEFT edge of the steps container to the LAST
    // dot's center, so dot-positions on the track are NOT evenly distributed
    // (dot 1 sits at 1/(2N-1), dot 2 at 3/(2N-1), … dot N at 1.0).
    const progressScale = this.computeFillScale(totalQty);
    if (this.progressFillEl) {
      this.progressFillEl.style.setProperty('--tryout-progress-scale', progressScale.toFixed(3));
    }

    // Step "reached" state — fill each milestone whose quota has been met.
    this.progressSteps.forEach((stepEl) => {
      const stepN = parseInt(stepEl.dataset.step, 10) || 0;
      stepEl.classList.toggle('tryout-bundle__progress-step--reached', totalQty >= stepN);
    });

    // Totals
    if (this.totalQtyEl) this.totalQtyEl.textContent = String(totalQty);
    if (this.totalListEl) this.totalListEl.textContent = this.formatMoney(totalListCents);

    // Savings line uses the active tier's percent
    const active = this.currentTier(totalQty);
    if (active && active.percent > 0) {
      const savingsCents = Math.round((totalListCents * active.percent) / 100);
      if (this.savingsWrap) this.savingsWrap.hidden = false;
      if (this.savingsAmountEl) this.savingsAmountEl.textContent = this.formatMoney(savingsCents);
    } else if (this.savingsWrap) {
      this.savingsWrap.hidden = true;
    }

    // Progress-message colour cue
    if (this.progressMessageEl) {
      this.progressMessageEl.classList.toggle('tryout-bundle__progress-message--unlocked', !!active);
    }

    if (!this.ctaBtn) return;

    if (active) {
      // Customer is at or past the lowest tier — CTA is enabled.
      this.ctaBtn.disabled = false;
      const tpl = totalQty === 1 ? this.i18n.addOne : this.i18n.addMany;
      this.setCtaText(this.fmt(tpl, { count: totalQty }));

      // Progress message: nudge to next tier if one exists.
      if (this.progressMessageEl) {
        const next = this.nextTier(totalQty);
        if (next) {
          const remaining = next.qty - totalQty;
          this.progressMessageEl.textContent = this.fmt(this.i18n.unlockedNext, {
            count: remaining,
            percent: active.percent,
            nextPercent: next.percent,
          });
        } else {
          this.progressMessageEl.textContent = this.fmt(this.i18n.unlocked, { percent: active.percent });
        }
      }
      return;
    }

    // Below the lowest tier — CTA disabled.
    if (totalQty === 0) {
      this.ctaBtn.disabled = true;
      this.setCtaText(this.idleCtaLabel);
      if (this.progressMessageEl) this.progressMessageEl.textContent = this.idleProgressMessage;
      return;
    }

    // 1 to (tierMin - 1) items: nudge to first tier.
    const remaining = this.tierMin - totalQty;
    const firstTierPct = this.tiers[0].percent;
    const ctaTpl = remaining === 1 ? this.i18n.pickMoreOne : this.i18n.pickMoreMany;
    const progTpl = remaining === 1 ? this.i18n.progressOne : this.i18n.progressMany;
    this.ctaBtn.disabled = true;
    this.setCtaText(this.fmt(ctaTpl, { count: remaining, percent: firstTierPct }));
    if (this.progressMessageEl) {
      this.progressMessageEl.textContent = this.fmt(progTpl, { count: remaining, percent: firstTierPct });
    }
  }

  setCtaText(text) {
    if (this.ctaLabelEl) this.ctaLabelEl.textContent = text;
    else if (this.ctaBtn) this.ctaBtn.textContent = text;
  }

  formatMoney(cents) {
    // Match the theme's locale formatting (€XX.XX with European separators
    // is handled by Shopify-side strings, but for client-side display we
    // use a plain euro format consistent with what the picker shows on load).
    return '€' + (cents / 100).toFixed(2);
  }

  /**
   * Single-roundtrip cart-add. POSTs all selected variants to /cart/add.js
   * with a `sections` parameter so the response includes the rendered
   * cart-drawer + cart-icon-bubble HTML. Hands the response to the theme's
   * <cart-drawer> for in-place re-render and open. No navigation, no extra
   * /cart.js fetch.
   */
  async handleAddToCart() {
    if (this.ctaBtn.disabled) return;

    const items = [];
    this.state.forEach((qty, variantId) => {
      if (qty > 0) items.push({ id: parseInt(variantId, 10), quantity: qty });
    });
    if (items.length === 0) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer
      ? cartDrawer.getSectionsToRender ? cartDrawer.getSectionsToRender().map(s => s.id) : ['cart-drawer', 'cart-icon-bubble']
      : [];

    const originalLabel = this.ctaLabelEl?.textContent;
    this.ctaBtn.disabled = true;
    this.setCtaText(this.i18n.adding);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${res.status}`);
      }
      const response = await res.json();

      // Hand the rendered sections to the cart drawer; it both re-renders the
      // drawer markup AND updates the cart-icon-bubble in the header (both are
      // listed in cart-drawer.getSectionsToRender). One render, one open.
      //
      // We deliberately DO NOT call window.publish(PUB_SUB_EVENTS.cartUpdate, …)
      // here. The cart-drawer-items subscriber in assets/cart.js fires its own
      // /cart?section_id=cart-drawer fetch on every cartUpdate event whose
      // source isn't 'cart-items' — that's what was causing the double network
      // round-trip and the visible cart-drawer flicker. Direct renderContents
      // is enough; the subscribers we'd notify (price-per-item, product-info)
      // aren't on this single-purpose conversion page anyway.
      if (cartDrawer && response.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(response);
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
      // If neither path exists, do NOT navigate to /cart — the theme uses the
      // drawer exclusively, navigating away would be worse than a silent fail.
    } catch (e) {
      console.error('[tryout-bundle] add to cart failed', e);
      this.setCtaText(this.i18n.error);
      setTimeout(() => {
        if (originalLabel) this.setCtaText(originalLabel);
        this.recompute();
      }, 2500);
      return;
    }

    // After successful add, restore the ready-to-add label so the customer
    // can keep building if they want to add another bundle.
    setTimeout(() => this.recompute(), 100);
  }
}

if (!customElements.get('tryout-bundle')) {
  customElements.define('tryout-bundle', TryoutBundle);
}
